import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { getApiClient } from '@apps/web/lib/api_client';
import { cn } from '@apps/web/lib/utils';

export type MessageAttachment = {
    readonly id: string;
    readonly content_type: string | null;
    readonly storage_status: 'pending' | 'ready' | 'rejected' | 'unavailable';
};

type MessageAttachmentsProps = {
    readonly attachments: ReadonlyArray<MessageAttachment>;
    readonly isInbound: boolean;
    readonly organizationId?: string | null;
};

export function MessageAttachments({
    attachments,
    isInbound,
    organizationId,
}: MessageAttachmentsProps) {
    const [contentUrls, setContentUrls] = React.useState<
        Readonly<Record<string, string>>
    >({});
    const [failedAttachmentIds, setFailedAttachmentIds] = React.useState<
        ReadonlySet<string>
    >(new Set());
    const [viewerAttachmentId, setViewerAttachmentId] = React.useState<
        string | null
    >(null);
    // Blob URLs live in a ref so they survive across effect runs: a ready-set
    // change must only fetch what is new, never tear down loaded siblings.
    const loadedUrlsRef = React.useRef(new Map<string, string>());
    // Keyed on the *sorted* ready ids (not the attachments array identity):
    // conversation polling hands down a fresh array every revalidation, and
    // composer uploads have no stable position, so both identity and order
    // are noise. Ids are UUIDs, so the comma join is unambiguous.
    const readyAttachmentIdsKey = attachments
        .filter((attachment) => attachment.storage_status === 'ready')
        .map((attachment) => attachment.id)
        .sort()
        .join(',');

    React.useEffect(() => {
        const loadedUrls = loadedUrlsRef.current;
        return () => {
            loadedUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
            loadedUrls.clear();
        };
    }, []);

    React.useEffect(() => {
        const loadedUrls = loadedUrlsRef.current;
        const readyAttachmentIds =
            readyAttachmentIdsKey && organizationId
                ? readyAttachmentIdsKey.split(',')
                : [];
        const readyIds = new Set(readyAttachmentIds);

        // Fetch incrementally: multi-image MMS ingest flips attachments to
        // ready one at a time, and that must not refetch loaded siblings or
        // close an open viewer. Only ids that left the ready set are revoked.
        for (const [attachmentId, objectUrl] of loadedUrls) {
            if (!readyIds.has(attachmentId)) {
                URL.revokeObjectURL(objectUrl);
                loadedUrls.delete(attachmentId);
            }
        }
        setContentUrls(Object.fromEntries(loadedUrls));
        setFailedAttachmentIds(
            (currentIds) =>
                new Set(
                    [...currentIds].filter((attachmentId) =>
                        readyIds.has(attachmentId),
                    ),
                ),
        );

        if (!organizationId) {
            return;
        }

        const abortController = new AbortController();
        const missingAttachmentIds = readyAttachmentIds.filter(
            (attachmentId) => !loadedUrls.has(attachmentId),
        );

        void Promise.all(
            missingAttachmentIds.map(async (attachmentId) => {
                try {
                    const response = await getApiClient().raw({
                        method: 'GET',
                        url: '/organizations/{organization_id}/conversations/attachments/{attachment_id}/content',
                        pathParams: {
                            organization_id: organizationId,
                            attachment_id: attachmentId,
                        },
                        reason: 'message-attachment-thumbnail',
                        signal: abortController.signal,
                    });
                    const blob = await response.blob();
                    const objectUrl = URL.createObjectURL(blob);
                    if (abortController.signal.aborted) {
                        URL.revokeObjectURL(objectUrl);
                        return;
                    }

                    loadedUrls.set(attachmentId, objectUrl);
                    setContentUrls((currentUrls) => ({
                        ...currentUrls,
                        [attachmentId]: objectUrl,
                    }));
                    // A retry after an earlier failure (a later ready-set
                    // change refetches ids that never loaded) must clear the
                    // stale failure so the image renders.
                    setFailedAttachmentIds((currentIds) => {
                        if (!currentIds.has(attachmentId)) {
                            return currentIds;
                        }
                        const nextIds = new Set(currentIds);
                        nextIds.delete(attachmentId);
                        return nextIds;
                    });
                } catch (error) {
                    if (!abortController.signal.aborted) {
                        console.error(
                            'Failed to load message attachment',
                            error,
                        );
                        setFailedAttachmentIds((currentIds) => {
                            const nextIds = new Set(currentIds);
                            nextIds.add(attachmentId);
                            return nextIds;
                        });
                    }
                }
            }),
        );

        return () => {
            abortController.abort();
        };
    }, [readyAttachmentIdsKey, organizationId]);

    const viewableAttachments = React.useMemo(
        () =>
            attachments.filter(
                (attachment) =>
                    contentUrls[attachment.id] !== undefined &&
                    !failedAttachmentIds.has(attachment.id),
            ),
        [attachments, contentUrls, failedAttachmentIds],
    );
    const viewerIndex =
        viewerAttachmentId === null
            ? -1
            : viewableAttachments.findIndex(
                  (attachment) => attachment.id === viewerAttachmentId,
              );
    const viewerAttachment =
        viewerIndex === -1 ? undefined : viewableAttachments[viewerIndex];
    const viewerUrl =
        viewerAttachment === undefined
            ? undefined
            : contentUrls[viewerAttachment.id];

    React.useEffect(() => {
        if (viewerAttachmentId !== null && viewerIndex === -1) {
            setViewerAttachmentId(null);
        }
    }, [viewerAttachmentId, viewerIndex]);

    // Single step function shared by the arrow keys and the chevron buttons
    // so the two navigation paths cannot diverge. A missing current id (the
    // open image just failed) keeps the selection instead of jumping.
    const stepViewer = React.useCallback(
        (delta: number) => {
            setViewerAttachmentId((currentAttachmentId) => {
                if (currentAttachmentId === null) {
                    return null;
                }
                const currentIndex = viewableAttachments.findIndex(
                    (attachment) => attachment.id === currentAttachmentId,
                );
                if (currentIndex === -1) {
                    return currentAttachmentId;
                }
                const nextIndex = Math.min(
                    Math.max(currentIndex + delta, 0),
                    viewableAttachments.length - 1,
                );
                return (
                    viewableAttachments[nextIndex]?.id ?? currentAttachmentId
                );
            });
        },
        [viewableAttachments],
    );

    React.useEffect(() => {
        // Register only when there is somewhere to navigate: with a single
        // image the viewer must not capture arrow keys away from the app.
        if (
            viewerAttachmentId === null ||
            viewerIndex === -1 ||
            viewableAttachments.length < 2
        ) {
            return;
        }

        function handleViewerKeyDown(event: KeyboardEvent) {
            // Modified arrows are browser/system shortcuts (history back,
            // selection, word-jump), not viewer navigation.
            if (
                event.defaultPrevented ||
                event.altKey ||
                event.ctrlKey ||
                event.metaKey ||
                event.shiftKey
            ) {
                return;
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                event.stopPropagation();
                stepViewer(-1);
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                event.stopPropagation();
                stepViewer(1);
            }
        }

        document.addEventListener('keydown', handleViewerKeyDown, true);
        return () =>
            document.removeEventListener('keydown', handleViewerKeyDown, true);
    }, [viewerAttachmentId, viewerIndex, viewableAttachments, stepViewer]);

    function renderAttachment(attachment: MessageAttachment) {
        if (
            attachment.storage_status !== 'ready' ||
            !organizationId ||
            failedAttachmentIds.has(attachment.id)
        ) {
            return (
                <div
                    className={cn(
                        'flex min-h-24 items-center justify-center rounded-lg border border-dashed px-4 text-xs',
                        isInbound
                            ? 'text-muted-foreground'
                            : 'border-white/40 text-white/80',
                    )}
                >
                    {attachment.storage_status === 'pending'
                        ? 'Processing image…'
                        : 'Image unavailable'}
                </div>
            );
        }

        const contentUrl = contentUrls[attachment.id];
        if (contentUrl === undefined) {
            return (
                <div className="text-muted-foreground flex min-h-24 items-center justify-center rounded-lg border border-dashed px-4 text-xs">
                    Loading image…
                </div>
            );
        }

        const viewableIndex = viewableAttachments.findIndex(
            (viewableAttachment) => viewableAttachment.id === attachment.id,
        );
        const hasMultipleImages = viewableAttachments.length > 1;

        return (
            <button
                aria-label={
                    hasMultipleImages
                        ? `Open image ${viewableIndex + 1} of ${viewableAttachments.length}`
                        : 'Open image'
                }
                className="focus-visible:ring-ring cursor-zoom-in rounded-lg focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden"
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setViewerAttachmentId(attachment.id);
                }}
                type="button"
            >
                <img
                    alt="Message attachment"
                    className="max-h-80 w-full rounded-lg object-contain"
                    loading="lazy"
                    onError={() => {
                        setFailedAttachmentIds((currentIds) => {
                            const nextIds = new Set(currentIds);
                            nextIds.add(attachment.id);
                            return nextIds;
                        });
                    }}
                    src={contentUrl}
                />
            </button>
        );
    }

    return (
        <React.Fragment>
            <div className="mb-2 grid max-w-sm gap-2">
                {attachments.map((attachment) => (
                    <React.Fragment key={attachment.id}>
                        {renderAttachment(attachment)}
                    </React.Fragment>
                ))}
            </div>
            <Dialog
                open={viewerAttachmentId !== null && viewerIndex !== -1}
                onOpenChange={(open) => {
                    if (!open) {
                        setViewerAttachmentId(null);
                    }
                }}
            >
                <DialogContent
                    className="max-w-[min(96vw,1100px)] border-none bg-transparent p-0 shadow-none sm:max-w-[min(96vw,1100px)]"
                    onEscapeKeyDown={(event) => event.stopPropagation()}
                    onPointerDownOutside={(event) => event.stopPropagation()}
                    showCloseButton={true}
                >
                    <DialogTitle className="sr-only">
                        Message attachment viewer
                    </DialogTitle>
                    {viewerUrl !== undefined && viewerIndex !== -1 ? (
                        <div className="relative flex items-center justify-center">
                            <img
                                alt="Message attachment"
                                className="mx-auto max-h-[85vh] w-auto rounded-lg object-contain"
                                src={viewerUrl}
                            />
                            {viewableAttachments.length > 1 ? (
                                <React.Fragment>
                                    <Button
                                        aria-label="Previous image"
                                        className="absolute top-1/2 left-2 -translate-y-1/2"
                                        disabled={viewerIndex === 0}
                                        onClick={() => stepViewer(-1)}
                                        size="icon"
                                        type="button"
                                        variant="secondary"
                                    >
                                        <ChevronLeft />
                                    </Button>
                                    <Button
                                        aria-label="Next image"
                                        className="absolute top-1/2 right-2 -translate-y-1/2"
                                        disabled={
                                            viewerIndex ===
                                            viewableAttachments.length - 1
                                        }
                                        onClick={() => stepViewer(1)}
                                        size="icon"
                                        type="button"
                                        variant="secondary"
                                    >
                                        <ChevronRight />
                                    </Button>
                                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2 py-1 text-xs text-white">
                                        {viewerIndex + 1} /{' '}
                                        {viewableAttachments.length}
                                    </div>
                                </React.Fragment>
                            ) : null}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}
